import React, { Children, Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { defaultTo, path, pipe } from 'ramda';
import find from 'lodash/fp/find';
import RoleAwareLink from './RoleAwareLink';
import DropdownMenu, { DropdownMenuItem } from './DropdownMenu';

const isPrimaryItem = item => (
  item && (item.props.primary || item.props.primaryRight)
);

class SecondaryNavigation extends Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    mobile: PropTypes.bool,
    className: PropTypes.string,
  };

  static defaultProps = {
    mobile: false,
    className: '',
  };

  getActiveSecondaryItem() {
    return find((item) => {
      if (!this.props.mobile && isPrimaryItem(item)) return false;
      return item.props.active;
    })(Children.toArray(this.props.children));
  }

  getDropdownLabel() {
    return pipe(
      path(['props', 'label']),
      defaultTo('More')
    )(this.getActiveSecondaryItem());
  }

  getDropdownLabelActive() {
    return !!this.getActiveSecondaryItem();
  }

  getSecondaryItem(item) {
    if (this.props.mobile) {
      return React.cloneElement(
        item,
        { mobile: true }
      );
    }
    return item;
  }

  renderPrimaryItems() {
    if (this.props.mobile) return null;
    return Children.map(this.props.children, (item) => {
      if (!item || !item.props.primary) return null;
      return item;
    });
  }

  renderPrimaryRightItems() {
    if (this.props.mobile) return null;
    return Children.map(this.props.children, (item) => {
      if (!item || !item.props.primaryRight) return null;
      return item;
    });
  }

  renderSecondaryItems() {
    const dropdownItems = Children.map(this.props.children, (item) => {
      if (
        !item ||
        item.props.active ||
        (!this.props.mobile && isPrimaryItem(item))
      ) return null;
      return (
        <DropdownMenuItem key={item.key}>
          {this.getSecondaryItem(item)}
        </DropdownMenuItem>
      );
    });

    return (
      <div className={classnames({ 'u-flex-1': this.props.mobile })}>
        <div display-if={dropdownItems.length}>
          <DropdownMenu
            className={classnames('c-secondary-navigation__item c-secondary-navigation__item--dropdown', {
              'is-active': this.getDropdownLabelActive(),
              'u-1/1': this.props.mobile,
            })}
            label={this.getDropdownLabel()}
            labelClassName={classnames({ 'c-secondary-navigation__dropdown-label': this.props.mobile })}
            alignment={this.props.mobile ? 'left' : 'right'}
            width={this.props.mobile ? '100%' : 'auto'}
            showIcon={!this.props.mobile}
          >
            {dropdownItems}
          </DropdownMenu>
        </div>
        <div display-if={dropdownItems.length === 0 && this.getActiveSecondaryItem()}>
          {React.cloneElement(this.getActiveSecondaryItem(), { primary: true })}
        </div>
      </div>
    );
  }

  render() {
    return (
      <div
        className={classnames(
          'c-secondary-navigation',
          this.props.className,
          { 'c-secondary-navigation--mobile': this.props.mobile })}
      >
        {this.renderPrimaryItems()}
        {this.renderSecondaryItems()}
        {this.renderPrimaryRightItems()}
      </div>
    );
  }
}
const Item = ({ label, to, primary, primaryRight, active, mobile, className }) => {
  return (
    <RoleAwareLink
      className={classnames(
        'c-secondary-navigation__item', className, {
          'c-secondary-navigation__item--primary': primary || primaryRight,
          'c-secondary-navigation__item--mobile': mobile,
          'is-active': active,
        })}
      to={to}
    >
      {label}
    </RoleAwareLink>
  );
};
Item.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  to: PropTypes.string.isRequired,
  primary: PropTypes.bool,
  primaryRight: PropTypes.bool,
  active: PropTypes.bool,
  mobile: PropTypes.bool,
  className: PropTypes.string,
};
Item.defaultProps = {
  primary: false,
  primaryRight: false,
  active: false,
  mobile: false,
  className: '',
};

SecondaryNavigation.Item = Item;
export default SecondaryNavigation;
